import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../redux-store/CustomReduxHooks";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateLoadingState } from "../Feature_Loading/Loading_Slice";
import NotFound from "../../pages/NotFound/NotFound";
import {
  E_ChannelOptions,
  T_LiRouteState,
} from "../../globalTypes/GlobalTypes";
import UnableToNavigate from "../../pages/UnableToNavigate/UnableToNavigate";
import { isEmpty } from "lodash";
import {
  LiLobCode,
  liRedirectionFunctionalityCodes,
} from "../../utils/constants";
import CustomPolicyOrAccountSelect from "../../components/CustomPolicyOrAccountSelect/CustomPolicyOrAccountSelect";
import { UseLiBaseDataLoader } from "./BaseDataInit/LifeInsurance_Init_BaseData";
import ReKycPopup from "./Dashboard/ReKyc/ReKycPopup";
import { updateReKycFlags } from "./LifeInsurance_Slice";

const LifeInsuranceGuard = () => {
  const { state, pathname, search } = useLocation();
  const policyNumber = (state as T_LiRouteState)?.policyNumber;
  const { isFetching, error } = UseLiBaseDataLoader("primary", policyNumber);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const csAppSlice = useAppSelector((st) => st.CsAppSlice);
  const ssoSlice = useAppSelector((st) => st.SsoSlice.ssoData);
  const [reKycPopupOpen, setReKycPopupOpen] = useState<boolean>(false);

  const { isReKycCaseCreateCalled, dashboardDetails, policyDetails, reKyc } =
    useAppSelector((state) => state.LifeInsuranceSlice);

  const primaryUserDashboardDetails = useAppSelector(
    (st) => st.LifeInsuranceSlice.dashboardDetails
  );

  useEffect(() => {
    if (isReKycCaseCreateCalled) {
      dispatch(updateReKycFlags({ eligibility: false, mandatory: false }));
    }
  }, [isReKycCaseCreateCalled]);

  useEffect(() => {
    if (!isEmpty(policyDetails) && reKyc.eligibility && reKyc.mandatory) {
      setReKycPopupOpen(true);
    }
  }, [policyDetails, reKyc.eligibility, reKyc.mandatory]);

  useEffect(() => {
    if (isFetching) {
      dispatch(updateLoadingState(true));
    } else {
      dispatch(updateLoadingState(false));
    }
  }, [isFetching]);

  const onPolicyNumberSelection = (policyNumber: string) => {
    navigate(pathname + search, { replace: true, state: { policyNumber } });
  };

  if (!policyNumber && !isEmpty(primaryUserDashboardDetails)) {
    return (
      <CustomPolicyOrAccountSelect
        data={{
          isOpen: true,
          options: primaryUserDashboardDetails.map(
            (item) => item.POLICY_NUMBER
          ),
          lobDetails: {
            lobId: LiLobCode,
            lobName: "",
            lob: "",
            uniqueId: true,
          },
          redirectionUrl: "",
        }}
        policyOrAccountDataHandler={onPolicyNumberSelection}
        // setIsPolicySelectionOpen={setIsPolicySelectionOpen}
        fromGuard={true}
      />
    );
  }

  if (reKycPopupOpen && reKyc.eligibility && reKyc.mandatory) {
    return (
      <ReKycPopup
        open={reKycPopupOpen}
        handleClose={() => setReKycPopupOpen(false)}
      />
    );
  }

  if (
    !reKycPopupOpen &&
    !isEmpty(policyDetails) &&
    reKyc.eligibility &&
    reKyc.mandatory
  ) {
    return <UnableToNavigate />;
  }

  return error ? (
    ssoSlice.functionality &&
    liRedirectionFunctionalityCodes.includes(ssoSlice.functionality) ? (
      <UnableToNavigate />
    ) : (
      <NotFound
        hideButton={csAppSlice.channel != E_ChannelOptions.CSAPP}
        moduleName="LI"
        pageName="LifeInsuranceGuard"
        errorTitle=""
        errorMessage={error}
      />
    )
  ) : isFetching ||
    isEmpty(policyNumber) ||
    (reKyc.eligibility && reKyc.mandatory) ? (
    <></>
  ) : (
    <Outlet />
  );
};

export default LifeInsuranceGuard;
